import './App.css';
import { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';

import LeadForm from './components/LeadForm';
import CloudArchitect from './pages/CloudArchitect';
import CloudArchitect1 from './pages/CloudArchitect1';
import GovernanceAudit from './pages/GovernanceAudit';
import MetricAgent from './pages/MetricAgent';
import ETLAgent from './pages/ETLAgent';
import InterviewAgent from './pages/InterviewAgent';
import DocParsing from './pages/DocParsing';
import SQLAgent from './pages/SQLAgent';
import DBAAgent from './pages/DBAAgent';
import LLMAsJudge from './pages/LLMAsJudge';
import WebScraper from './pages/WebScraper';
import EmailAssistant from './pages/EmailAssistant';
import KYCOnboarding from './pages/KYCOnboarding';
import CPA from './pages/CPA';
import Scheduler from './pages/Scheduler';
import Summarizer from './pages/Summarizer';
import SchemaGovernanceDesigner from './pages/SchemaGovernanceDesigner';
import DataPlatformReviewer from './pages/DataPlatformReviewer';
import DataEngineerAgent from './pages/DataEngineerAgent';
import RecruiterAgent from './pages/RecruiterAgent';
import SDRAgent from './pages/SDRAgent';
import MarketingAgent from './pages/MarketingAgent';
import MarketingAutonomous from './pages/MarketingAutonomous';
import AEOAudit from './pages/AEOAudit';
import PrivacyPolicy from './pages/PrivacyPolicy';
import DigitalWorkforce from './components/home/DigitalWorkforce';
import EcommerceReport from './pages/EcommerceReport';
// New industry pages
import SingaporeAIAgents from './pages/industries/SingaporeAIAgents';
import SoutheastAsiaAIAgents from './pages/industries/SoutheastAsiaAIAgents';
import FinancialServicesAI from './pages/industries/FinancialServicesAI';
import HRRecruitmentAI from './pages/industries/HRRecruitmentAI';
// New use-case pages
import ReduceOperationalCosts from './pages/use-cases/ReduceOperationalCosts';
import BuildGenAISolutions from './pages/use-cases/BuildGenAISolutions';
import AIAgentImplementation from './pages/use-cases/AIAgentImplementation';
// Comparison page
import AIAgentsVsTraditional from './pages/compare/AIAgentsVsTraditional';
import FractionalCAIO from './pages/FractionalCAIO';
import HealthcareAI from './pages/industries/HealthcareAI';
import LogisticsAI from './pages/industries/LogisticsAI';
import RetailAI from './pages/industries/RetailAI';
import PublicSectorAI from './pages/industries/PublicSectorAI';
import ComplianceAutomation from './pages/use-cases/ComplianceAutomation';
import DocumentAI from './pages/use-cases/DocumentAI';
import PredictiveAnalyticsAI from './pages/use-cases/PredictiveAnalyticsAI';
import SecurityGovernance from './pages/SecurityGovernance';
import ImplementationApproach from './pages/ImplementationApproach';
import CopilotFinanceTraining from './pages/CopilotFinanceTraining';
import ClaudeCoworkTraining from './pages/ClaudeCoworkTraining';
import AmazonQTraining from './pages/AmazonQTraining';
import GeminiEnterpriseTraining from './pages/GeminiEnterpriseTraining';
import CopilotManufacturingTraining from './pages/CopilotManufacturingTraining';
import CopilotHRTraining from './pages/CopilotHRTraining';
import ChatGPTEnterpriseTraining from './pages/ChatGPTEnterpriseTraining';
import AIProcessAssessment from './pages/AIProcessAssessment';
import { captureAttribution } from './lib/attribution';
import { trackPageView } from './lib/analytics';
import LinkedInInsight from './components/LinkedInInsight';
import CookieConsent from './components/CookieConsent';
import { resetConsent } from './lib/consent';
if (typeof window !== 'undefined') {
  window.__EAGENTIX_BUILD_MARKER__ = '2026-05-12-static-cache-bust-2';
}

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    // Remember the last real content page so the homepage #contact form can
    // record where the lead actually came from (not just "/#contact").
    if (pathname && pathname !== '/') {
      try { sessionStorage.setItem('lead_origin', pathname); } catch (_) {}
    }
    // UTMs and click ids only exist on the landing URL. Capture them on first
    // sight so a lead submitted three pages later still carries its campaign.
    captureAttribution();
    // gtag('config') only fires on first load; React Router navigation does not
    // re-trigger it. Send the page view explicitly. Deferred a tick so
    // document.title has been updated by the route's <SEO> helmet.
    const t = setTimeout(() => trackPageView(pathname), 50);
    return () => clearTimeout(t);
  }, [pathname]);
  useEffect(() => {
    if (hash) {
      setTimeout(() => {
        const id = hash.replace('#', '');
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    } else {
      window.scrollTo(0, 0);
    }
  }, [pathname, hash]);
  return null;
}


// SVG Icons
const Icons = {
  Brain: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 5a3 3 0 1 0-5.997.125 4 4 0 0 0-2.526 5.77 4 4 0 0 0 .556 6.588A4 4 0 1 0 12 18Z" />
      <path d="M12 5a3 3 0 1 1 5.997.125 4 4 0 0 1 2.526 5.77 4 4 0 0 1-.556 6.588A4 4 0 1 1 12 18Z" />
      <path d="M15 13a4.5 4.5 0 0 1-3-4 4.5 4.5 0 0 1-3 4" />
    </svg>
  ),
  Strategy: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
      <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
    </svg>
  ),
  Platform: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="3" width="20" height="14" rx="2" />
      <line x1="8" x2="16" y1="21" y2="21" />
      <line x1="12" x2="12" y1="17" y2="21" />
    </svg>
  ),
  Work: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 20V4a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
      <rect width="20" height="14" x="2" y="6" rx="2" />
    </svg>
  ),
  Shield: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
      <path d="m9 12 2 2 4-4" />
    </svg>
  ),
  Scale: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
    </svg>
  ),
  Users: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  ),
  Target: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <circle cx="12" cy="12" r="6" />
      <circle cx="12" cy="12" r="2" />
    </svg>
  ),
  Zap: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
    </svg>
  ),
  Check: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 6 9 17l-5-5" />
    </svg>
  ),
  ArrowRight: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 12h14" />
      <path d="m12 5 7 7-7 7" />
    </svg>
  ),
  Layers: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="12 2 2 7 12 12 22 7 12 2" />
      <polyline points="2 17 12 22 22 17" />
      <polyline points="2 12 12 17 22 12" />
    </svg>
  ),
  Menu: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="4" x2="20" y1="12" y2="12" />
      <line x1="4" x2="20" y1="6" y2="6" />
      <line x1="4" x2="20" y1="18" y2="18" />
    </svg>
  ),
  LinkedIn: ({ color = "currentColor" }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
      <rect x="2" y="9" width="4" height="12" />
      <circle cx="4" cy="4" r="2" />
    </svg>
  ),
  Mail: ({ color = "currentColor" }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect width="20" height="16" x="2" y="4" rx="2" />
      <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
    </svg>
  ),
  YouTube: ({ color = "currentColor" }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2.5 17a24.12 24.12 0 0 1 0-10 2 2 0 0 1 1.4-1.4 49.56 49.56 0 0 1 16.2 0A2 2 0 0 1 21.5 7a24.12 24.12 0 0 1 0 10 2 2 0 0 1-1.4 1.4 49.55 49.55 0 0 1-16.2 0A2 2 0 0 1 2.5 17" />
      <path d="m10 15 5-3-5-3z" />
    </svg>
  ),
  MapPin: ({ color = "currentColor" }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  ),
  Facebook: ({ color = "currentColor" }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
    </svg>
  ),
  Twitter: ({ color = "currentColor" }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 4s-.7 2.1-2 3.4c1.6 10-9.4 17.3-18 11.6 2.2.1 4.4-.6 6-2C3 15.5.5 9.6 3 5c2.2 2.6 5.6 4.1 9 4-.9-4.2 4-6.6 7-3.8 1.1 0 3-1.2 3-1.2z" />
    </svg>
  ),
  Instagram: ({ color = "currentColor" }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect width="20" height="20" x="2" y="2" rx="5" ry="5" />
      <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
      <line x1="17.5" x2="17.51" y1="6.5" y2="6.5" />
    </svg>
  ),
  Calendar: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  ),
};

// ChatGPT Style Components
const GlowOrb = () => (
  <div className="glow-orb-container">
    <div className="glow-orb"></div>
    <div className="glow-orb-inner"></div>
  </div>
);

// Obfuscated Email Component
const EmailLink = ({ className, children }) => {
  const user = 'contact';
  const domain = 'eagentix.com';

  const handleClick = (e) => {
    e.preventDefault();
    window.location.href = `mailto:${user}@${domain}`;
  };

  return (
    <a
      href={`mailto:${user}@${domain}`}
      onClick={handleClick}
      className={className}
    >
      {children || `${user}@${domain}`}
    </a>
  );
};

// Hero Section - High-Fidelity Cinematic Space Theme
function Hero() {
  return (
    <section className="hero" id="home">
      <div className="hero-video-bg">
        <video
          key="hero-video-mux"
          autoPlay
          muted
          loop
          playsInline
          preload="none"
          className="hero-video"
          poster="https://eagentix.com/assets/platform.png"
        >
          <source
            src="https://d8j0ntlcm91z4.cloudfront.net/user_38xzZboKViGWJOttwIXH07lWA1P/hf_20260217_030345_246c0224-10a4-422c-b324-070b7c0eceda.mp4"
            type="video/mp4"
          />
          <source
            src="https://eagentix.com/assets/hero-bg.mov"
            type="video/mp4"
          />
        </video>
        <div className="hero-overlay"></div>
      </div>

      <div className="hero-content">
        <div className="hero-badge animate-fade-in-up">
          AI CONSULTING SINGAPORE · ENTERPRISE AI AGENTS
        </div>

        <h1 className="hero-title animate-fade-in-up">
          Enterprise AI Agents for<br />
          <span className="hero-title-gradient">Finance, Operations & Marketing.</span>
        </h1>

        <p className="hero-description animate-fade-in-up delay-100">
          Strategic AI consulting meets cutting-edge autonomous technology. We build 
          purpose-built AI agents that automate finance, marketing, accounting, and complex business workflows.
        </p>

        <div className="hero-cta-group animate-fade-in-up delay-200">
          <a href="#contact" className="btn btn-primary">
            Start Building
            <Icons.ArrowRight />
          </a>
          <a href="/industries/singapore-enterprise-ai-agents" className="btn btn-ghost">
            Singapore AI Solutions
            <Icons.ArrowRight />
          </a>
        </div>
      </div>
    </section>
  );
}

// Navbar Component
function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { pathname } = useLocation();
  const onAssessmentPage = pathname === '/ai-process-assessment';
  const onFinancialServicesPage = pathname === '/industries/financial-services-ai-agents';
  const navbarCtaLabel = onAssessmentPage
    ? 'Check fit and book'
    : onFinancialServicesPage
      ? 'Book a Finance Session'
      : 'Get a Free Assessment';

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <nav className={`navbar ${scrolled ? 'scrolled' : ''}`}>
      <div className="container navbar-container">
        <Link to="/" className="navbar-logo">
          <img
            src="/logo-transparent.png?v=3"
            alt="Eagentix Logo - Enterprise AI Agents"
            className="navbar-logo-img"
          />
        </Link>

        <ul className={`navbar-nav ${mobileOpen ? 'open' : ''}`} id="nav-menu">
          <li><Link to="/marketing" onClick={() => setMobileOpen(false)} className="navbar-link">Marketing</Link></li>
          <li><Link to="/industries/financial-services-ai-agents" onClick={() => setMobileOpen(false)} className="navbar-link">Finance</Link></li>
          <li><Link to="/use-cases/reduce-operational-costs" onClick={() => setMobileOpen(false)} className="navbar-link">Operations</Link></li>
          <li><Link to="/fractional-caio" onClick={() => setMobileOpen(false)} className="navbar-link">AI Leadership</Link></li>
          <li className="nav-dropdown-trigger">
            <span className="navbar-link">Enterprise AI Upskilling</span>
            <ul className="nav-dropdown">
              <div style={{ padding: '8px 16px 4px', fontSize: '0.75rem', fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Microsoft Copilot</div>
              <li><Link to="/copilot-finance-training" onClick={() => setMobileOpen(false)}>Copilot for Finance</Link></li>
              <li><Link to="/copilot-manufacturing-training" onClick={() => setMobileOpen(false)}>Copilot for Manufacturing</Link></li>
              <li><Link to="/copilot-hr-training" onClick={() => setMobileOpen(false)}>Copilot for HR</Link></li>
              <div style={{ height: '1px', background: '#f1f5f9', margin: '4px 16px' }}></div>
              <li><Link to="/chatgpt-enterprise-training" onClick={() => setMobileOpen(false)}>ChatGPT Enterprise</Link></li>
              <li><Link to="/claude-cowork-training" onClick={() => setMobileOpen(false)}>Claude Cowork</Link></li>
              <li><Link to="/amazon-q-training" onClick={() => setMobileOpen(false)}>Amazon Q</Link></li>
              <li><Link to="/gemini-enterprise-training" onClick={() => setMobileOpen(false)}>Gemini Enterprise</Link></li>
            </ul>
          </li>
          <li><Link
            to={onAssessmentPage
              ? '/ai-process-assessment#assessment-form'
              : onFinancialServicesPage
                ? '/industries/financial-services-ai-agents#contact'
                : '/ai-process-assessment'}
            onClick={() => {
              setMobileOpen(false);
              if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
                window.gtag('event', 'cta_click', { cta_label: navbarCtaLabel, page: pathname });
              }
            }}
            className="navbar-cta"
          >
            <Icons.Calendar />
            {navbarCtaLabel}
            <Icons.ArrowRight />
          </Link></li>
        </ul>

        <button
          className="navbar-mobile-toggle"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label={mobileOpen ? "Close menu" : "Open menu"}
        >
          <Icons.Menu />
        </button>
      </div>
    </nav>
  );
}

// Integration Section (Refactored: The Agent Harness)
function Integration() {
  const harnessLayers = [
    {
      title: 'Model Intelligence',
      desc: 'Route each task to the right frontier or sovereign model, then constrain the output with role-specific instructions and enterprise policy.',
      icon: <Icons.Brain />,
      accent: '#60a5fa',
      metric: 'Model routing',
      chips: ['OpenAI', 'Claude', 'Mistral', 'Azure AI']
    },
    {
      title: 'Context & Memory',
      desc: 'Inject institutional knowledge, project files, prior decisions, standards, and domain playbooks so the agent works from company truth.',
      icon: <Icons.Layers />,
      accent: '#34d399',
      metric: 'Durable state',
      chips: ['Files', 'Vector search', 'SQL', 'Knowledge bases']
    },
    {
      title: 'Tools & Execution',
      desc: 'Give agents controlled access to APIs, browsers, code execution, workflows, and business systems so recommendations become work.',
      icon: <Icons.Zap />,
      accent: '#f59e0b',
      metric: 'Action layer',
      chips: ['MCP', 'APIs', 'Browser', 'Automation']
    },
    {
      title: 'Safe Runtime',
      desc: 'Run work inside governed environments with scoped access, audit trails, approvals, and deployment options from cloud to air-gapped.',
      icon: <Icons.Shield />,
      accent: '#38bdf8',
      metric: 'Governed execution',
      chips: ['Sandbox', 'RBAC', 'Audit', 'On-prem']
    },
    {
      title: 'Feedback Sensors',
      desc: 'Use deterministic checks and inferential review loops to catch drift, validate quality, and push errors back before humans review.',
      icon: <Icons.Target />,
      accent: '#a78bfa',
      metric: 'Self-correction',
      chips: ['Evals', 'Tests', 'Logs', 'LLM judge']
    },
    {
      title: 'Human Steering',
      desc: 'Keep experts in the loop for policy, risk, escalation, and final judgment while the harness removes repetitive review toil.',
      icon: <Icons.Users />,
      accent: '#fb7185',
      metric: 'Expert control',
      chips: ['Approvals', 'Escalation', 'Review', 'Governance']
    }
  ];

  const controlLoops = [
    'Feedforward guides: policies, standards, skills, templates, and domain rules before the agent acts.',
    'Feedback sensors: tests, logs, evals, code review, security checks, and AI judges after the agent acts.',
    'Steering loop: repeated issues become new rules, sensors, or workflows so the harness improves over time.'
  ];

  return (
    <section className="integration" id="platform" style={{ padding: '110px 0', background: '#07111f', color: 'white', position: 'relative', overflow: 'hidden' }}>
      {/* Background Aurora Effect */}
      <div style={{ position: 'absolute', top: '-20%', left: '8%', width: '520px', height: '520px', background: 'radial-gradient(circle, rgba(14, 165, 233, 0.24) 0%, transparent 68%)', filter: 'blur(40px)', zIndex: 0 }} />
      <div style={{ position: 'absolute', bottom: '-24%', right: '5%', width: '620px', height: '620px', background: 'radial-gradient(circle, rgba(16, 185, 129, 0.16) 0%, transparent 70%)', filter: 'blur(50px)', zIndex: 0 }} />
      <div style={{ position: 'absolute', inset: 0, backgroundImage: 'linear-gradient(rgba(255,255,255,0.035) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.035) 1px, transparent 1px)', backgroundSize: '42px 42px', maskImage: 'linear-gradient(to bottom, transparent, black 18%, black 78%, transparent)', opacity: 0.35 }} />
      
      <div className="container" style={{ position: 'relative', zIndex: 1 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 420px), 1fr))', gap: '42px', alignItems: 'end', marginBottom: '58px' }}>
          <div>
            <span style={{ color: '#7dd3fc', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.22em', fontSize: '0.74rem' }}>Enterprise Agent Harness</span>
            <h2 style={{ fontSize: 'clamp(2.5rem, 5vw, 4.8rem)', lineHeight: 0.95, fontWeight: 950, marginTop: '18px', marginBottom: '24px', letterSpacing: '-0.065em', color: 'white' }}>
              The system that turns models into accountable digital workers.
            </h2>
            <p style={{ fontSize: '1.18rem', color: '#f8fafc', maxWidth: '760px', lineHeight: 1.7, margin: 0, fontWeight: 500, textShadow: '0 1px 20px rgba(15, 23, 42, 0.85)' }}>
              A raw model can answer. A harness gives it memory, tools, operating boundaries, validation loops, and human steering so enterprises can move from demos to reliable work.
            </p>
          </div>
          <div style={{ background: 'linear-gradient(145deg, rgba(8, 18, 34, 0.98), rgba(17, 31, 55, 0.92))', border: '1px solid rgba(125, 211, 252, 0.24)', borderRadius: '30px', padding: '26px', boxShadow: '0 30px 90px rgba(0,0,0,0.32)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', marginBottom: '22px' }}>
              <div style={{ color: '#e2e8f0', fontWeight: 900, fontSize: '1.05rem' }}>Harness Control Loop</div>
              <div style={{ color: '#86efac', fontWeight: 900, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.16em' }}>Guided + Verified</div>
            </div>
            <div style={{ display: 'grid', gap: '14px' }}>
              {controlLoops.map((item, idx) => (
                <div key={idx} style={{ display: 'grid', gridTemplateColumns: '34px 1fr', gap: '14px', alignItems: 'start', padding: '14px', borderRadius: '18px', background: 'rgba(255,255,255,0.075)', border: '1px solid rgba(255,255,255,0.13)' }}>
                  <div style={{ width: '34px', height: '34px', borderRadius: '50%', background: idx === 0 ? 'rgba(96, 165, 250, 0.18)' : idx === 1 ? 'rgba(52, 211, 153, 0.18)' : 'rgba(251, 113, 133, 0.18)', color: idx === 0 ? '#93c5fd' : idx === 1 ? '#86efac' : '#fda4af', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {idx === 0 ? <Icons.Layers /> : idx === 1 ? <Icons.Target /> : <Icons.Users />}
                  </div>
                  <p style={{ margin: 0, color: '#f1f5f9', lineHeight: 1.55, fontSize: '0.95rem', fontWeight: 600 }}>{item}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '18px' }}>
          {harnessLayers.map((layer, idx) => (
            <div key={idx} style={{ position: 'relative', overflow: 'hidden', minHeight: '292px', background: 'linear-gradient(180deg, rgba(255,255,255,0.095), rgba(255,255,255,0.045))', border: '1px solid rgba(255,255,255,0.14)', borderRadius: '30px', padding: '28px', backdropFilter: 'blur(14px)', boxShadow: '0 24px 80px rgba(0,0,0,0.22)' }}>
              <div style={{ position: 'absolute', top: '-90px', right: '-70px', width: '190px', height: '190px', borderRadius: '50%', background: `${layer.accent}25`, filter: 'blur(18px)' }} />
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', marginBottom: '28px', position: 'relative' }}>
                <div style={{ width: '54px', height: '54px', borderRadius: '18px', background: `${layer.accent}18`, color: layer.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 16px 36px ${layer.accent}18` }}>
                  {layer.icon}
                </div>
                <span style={{ color: layer.accent, fontWeight: 900, fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.16em' }}>{layer.metric}</span>
              </div>
              <h3 style={{ fontSize: '1.35rem', fontWeight: 900, color: 'white', marginBottom: '12px', letterSpacing: '-0.025em', position: 'relative' }}>{layer.title}</h3>
              <p style={{ color: '#f1f5f9', lineHeight: 1.62, marginBottom: '26px', position: 'relative', fontWeight: 500 }}>{layer.desc}</p>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', position: 'relative' }}>
                {layer.chips.map((chip) => (
                  <span key={chip} style={{ border: '1px solid rgba(255,255,255,0.11)', background: 'rgba(255,255,255,0.055)', color: '#e2e8f0', borderRadius: '999px', padding: '7px 10px', fontSize: '0.76rem', fontWeight: 800 }}>
                    {chip}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Central Orchestrator Visual */}
        <div style={{ marginTop: '72px', background: 'linear-gradient(135deg, rgba(14, 165, 233, 0.14), rgba(16, 185, 129, 0.10), rgba(15, 23, 42, 0.78))', borderRadius: '42px', padding: '34px', border: '1px solid rgba(255,255,255,0.12)', boxShadow: '0 34px 110px rgba(0,0,0,0.32)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 360px), 1fr))', gap: '30px', alignItems: 'center' }}>
            <div style={{ padding: '24px' }}>
              <span style={{ color: '#86efac', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.18em', fontSize: '0.72rem' }}>Why it matters</span>
              <div style={{ width: '72px', height: '72px', borderRadius: '24px', marginTop: '18px', background: 'linear-gradient(135deg, rgba(96, 165, 250, 0.24), rgba(52, 211, 153, 0.2))', color: '#bfdbfe', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 20px 60px rgba(14, 165, 233, 0.18)' }}>
                <Icons.Brain />
              </div>
              <h3 style={{ color: 'white', fontSize: 'clamp(2rem, 4vw, 3.25rem)', lineHeight: 1, letterSpacing: '-0.055em', margin: '14px 0 18px', fontWeight: 950 }}>
                A professional brain for enterprise work.
              </h3>
              <p style={{ color: '#f8fafc', lineHeight: 1.7, margin: 0, fontSize: '1.06rem', fontWeight: 500, textShadow: '0 1px 18px rgba(15, 23, 42, 0.8)' }}>
                The harness is the brain layer around the model: it remembers enterprise context, chooses the right tools, applies guardrails, checks its own work, and escalates when judgment matters. That means less supervision, fewer wasted cycles, and higher-grade agent output.
              </p>
            </div>
            <div style={{ background: 'rgba(2, 6, 23, 0.68)', border: '1px solid rgba(255,255,255,0.16)', borderRadius: '30px', padding: '24px' }}>
              {[
                ['01', 'Plan with context', 'Requirements, files, standards, memory, and approved playbooks are assembled before work begins.'],
                ['02', 'Act with constraints', 'Tools execute through scoped permissions, sandboxes, deterministic workflows, and audit trails.'],
                ['03', 'Verify before handoff', 'Tests, evals, security checks, logs, and expert escalation reduce avoidable human review.']
              ].map(([num, title, desc]) => (
                <div key={num} style={{ display: 'grid', gridTemplateColumns: '52px 1fr', gap: '16px', padding: '18px 0', borderBottom: num === '03' ? 'none' : '1px solid rgba(255,255,255,0.09)' }}>
                  <div style={{ color: '#0f172a', background: 'linear-gradient(135deg, #bfdbfe, #86efac)', borderRadius: '16px', width: '52px', height: '52px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 950 }}>{num}</div>
                  <div>
                    <div style={{ color: 'white', fontWeight: 900, marginBottom: '6px', fontSize: '1.02rem' }}>{title}</div>
                    <div style={{ color: '#f1f5f9', lineHeight: 1.55, fontSize: '0.95rem', fontWeight: 500 }}>{desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// Differentiators Section
function Differentiators() {
  const diffs = [
    "Strategy-First Consulting: We don't just deploy tech; we align enterprise AI agents with your core business objectives and operational KPIs.",
    "Autonomous Agent Orchestration: Building intelligent agents that don't just chat, but execute complex, multi-step business workflows.",
    "Deep System Integration: Seamlessly connecting AI to your existing ERPs, CRMs, and proprietary databases for true end-to-end automation.",
    "Governance & Guardrails: Leading AI consulting in Singapore ensuring every agent operates within strict enterprise security and ethical frameworks.",
    "Human-Agent Collaboration: Designing 'Human-in-the-loop' systems that blend AI efficiency with human judgment and oversight.",
  ];

  return (
    <section className="differentiators section" id="differentiators">
      <div className="container">
        <div className="section-header">
          <span className="section-label">Why Eagentix</span>
          <h2 className="section-title">The Eagentix Advantage</h2>
          <p className="section-description">Bridging the gap between bespoke AI strategy and robust, scalable agent automation.</p>
        </div>
        <div className="mission-grid">
          <div className="mission-content" style={{ textAlign: 'left' }}>
            <h3 style={{ fontSize: '1.75rem', fontWeight: 700, marginBottom: '16px', background: 'linear-gradient(135deg, #d97757 0%, #af52de 100%)', WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent', color: '#d97757' }}>
              Strategic Consulting Meets Production AI.
            </h3>
            <p style={{ textAlign: 'left', marginBottom: '24px', fontSize: '1.05rem', color: 'var(--color-text-primary)', lineHeight: 1.6 }}>
              We provide the strategic roadmap and technical expertise as a leading AI consulting firm in Singapore, turning automation concepts into production reality through our enterprise AI agents.
            </p>
            <StreamedPointList items={diffs} accent="#d97757" minHeight="320px" delayMs={500} />
          </div>
          <div className="infographic-container">
            <img
              alt="AI Consulting Singapore - Eagentix Differentiators"
              loading="lazy"
              decoding="async"
              width="1200"
              height="800"
              className="infographic-image"
              src="/assets/Empowering self-service - visual selection (1).webp"
              style={{ boxShadow: 'var(--shadow-xl)', border: '1px solid rgba(0,0,0,0.05)' }}
            />
          </div>
        </div>
      </div>
    </section>
  );
}

// Strategy Pillars Section (Formerly Services)
function StrategyPillars() {
  const services = [
    {
      title: "AI Strategy Consulting",
      focus: "Cross-Cloud Architecture & AI Readiness",
      desc: "Leading AI consulting in Singapore, architecting agentic systems that reason across the entire cloud spectrum. We design and validate high-fidelity architectures.",
      features: [
        "Specialized architecture for AWS, Azure, GCP, Alibaba Cloud, and OCI",
        "Well-Architected Framework reviews for agentic workloads",
        "Strategic AI transformation roadmaps for complex global infra",
        "Sovereign AI readiness and sovereign data residency planning",
      ],
      iconClass: "strategy",
      icon: <Icons.Strategy />,
    },
    {
      title: "Enterprise AI Agent Platform",
      focus: "Model-Agnostic Agentic Orchestration",
      desc: "Our proprietary platform provides the deterministic engine for your digital workforce, ensuring consistency, safety, and operational memory.",
      features: [
        "Platform Orchestration: Amazon Bedrock, Google Vertex AI, Azure AI Foundry",
        "Frontier Intelligence: Anthropic Claude 3.5, OpenAI GPT-4o, Mistral, DeepSeek",
        "Integrated Ecosystem: Native connectors to 100+ Enterprise ERP, CRM, and DBs",
        "Deterministic Safety: 7-Tier Risk Profiles and confidence gating",
      ],
      iconClass: "technology",
      icon: <Icons.Platform />,
    },
    {
      title: "Sovereign Deployment Ops",
      focus: "Secure, Scalable & Air-Gapped Intelligence",
      desc: "Deploy enterprise AI agents anywhere. From hyperscale public clouds to fully air-gapped on-premise hardware for maximum security.",
      features: [
        "Air-Gapped & On-Premise: Dell, HPE, and Red Hat certified deployment",
        "Policy-as-Code: Automated compliance and SOC2-ready audit trails",
        "Hybrid Cloud Orchestration: Seamless logic flow across on-prem and cloud",
        "Infinite Scaling: Elastic agent clusters designed for enterprise volume",
      ],
      iconClass: "scalable",
      icon: <Icons.Scale />,
    },
  ];

  return (
    <section className="services section" id="offerings">
      <div className="container">
        <div className="section-header">
          <span className="section-label">Enterprise AI Consulting</span>
          <h2 className="section-title">Singapore's Enterprise AI Partner</h2>
        </div>
        <div className="mission-grid" style={{ marginBottom: '64px' }}>
          <div className="mission-content" style={{ textAlign: 'left' }}>
            <h3 style={{ fontSize: '1.75rem', fontWeight: 700, marginBottom: '16px', backgroundClip: 'text', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', color: 'var(--color-primary)' }}>
              Beyond Simple Chatbots: Enterprise-Grade Agents.
            </h3>
            <p style={{ textAlign: 'left', marginBottom: '24px', fontSize: '1.05rem', color: 'var(--color-text-primary)', lineHeight: 1.6 }}>
              Eagentix engineers the infrastructure that makes enterprise AI agents work at scale. We bridge the gap between hyperscale cloud intelligence and the deterministic requirements of Singapore's enterprise operations.
            </p>
          </div>
          <div className="infographic-container" style={{ margin: 0 }}>
            <img
              alt="Enterprise AI Agents Singapore"
              loading="lazy"
              decoding="async"
              width="1200"
              height="800"
              src="/assets/platform.webp"
              style={{ boxShadow: 'var(--shadow-xl)', border: '1px solid rgba(0,0,0,0.05)', borderRadius: '24px' }}
            />
          </div>
        </div>
        <div className="services-grid">
          {services.map((s, i) => (
            <div key={i} className="service-card">
              <div className={`service-icon ${s.iconClass}`}>{s.icon}</div>
              <h3 className="service-title">{s.title}</h3>
              <p style={{ fontSize: '0.8rem', color: 'var(--color-primary)', fontWeight: 600, textTransform: 'uppercase', marginBottom: '12px' }}>{s.focus}</p>
              <p className="service-description">{s.desc}</p>
              <ul className="service-features" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {s.features.map((f, j) => (
                  <li key={j} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '12px' }}>
                    <Icons.Check style={{ flexShrink: 0, marginTop: '4px', color: '#10b981' }} />
                    <span style={{ fontSize: '0.95rem', color: '#4b5563', lineHeight: 1.5 }}>{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// Typewriter Component
const TypewriterLine = ({ children, isActive, isCompleted, onComplete }) => {
  const [text, setText] = useState('');
  const fullText = children;

  useEffect(() => {
    if (isCompleted) { setText(fullText); return; }
    if (!isActive) { setText(''); return; }
    let i = 0;
    const interval = setInterval(() => {
      if (i <= fullText.length) {
        setText(fullText.substring(0, i));
        i++;
      } else {
        clearInterval(interval);
        if (onComplete) onComplete();
      }
    }, 30);
    return () => clearInterval(interval);
  }, [isActive, isCompleted, fullText, onComplete]);

  return <div className="typewriter-line">{text}</div>;
};

const StreamedPointList = ({ items, accent = 'var(--color-primary)', minHeight = '300px', delayMs = 600 }) => {
  const [activeIdx, setActiveIdx] = useState(0);

  return (
    <div style={{ marginTop: '20px', minHeight }}>
      {items.map((item, i) => {
        const done = i < activeIdx;
        const active = i === activeIdx;
        const pending = i > activeIdx;

        return (
          <div
            key={i}
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '12px',
              marginBottom: '14px',
              padding: '12px 14px',
              borderRadius: '14px',
              background: !pending ? 'rgba(255,255,255,0.82)' : 'transparent',
              boxShadow: !pending ? 'rgba(15,23,42,0.06) 0 8px 20px -14px' : 'none',
              border: `1px solid ${!pending ? 'rgba(0,0,0,0.06)' : 'transparent'}`,
              opacity: pending ? 0.7 : 1,
              transition: '0.35s ease',
            }}
          >
            <div
              style={{
                width: '24px',
                height: '24px',
                borderRadius: '999px',
                background: !pending ? `${accent}18` : 'rgba(148,163,184,0.18)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: accent,
                flexShrink: 0,
                marginTop: '1px',
                boxShadow: !pending ? `0 0 0 1px ${accent}20` : 'none',
              }}
            >
              <div
                style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '999px',
                  background: pending ? 'rgba(148,163,184,0.45)' : accent,
                  boxShadow: pending ? 'none' : `0 0 10px ${accent}`,
                }}
              />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <TypewriterLine
                isActive={active}
                isCompleted={done}
                onComplete={active && i < items.length - 1 ? () => window.setTimeout(() => setActiveIdx(i + 1), delayMs) : undefined}
              >
                {item}
              </TypewriterLine>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// About / Identity Section
function Mission() {
  const points = [
    "AI Consulting Singapore: Focus on Enterprise Transformation, AI Readiness, and Data Maturity Assessment.",
    "Enterprise AI Agents: Multi-Model Orchestration and Sovereign Infrastructure via Eagentix platform.",
    "Proprietary Platform Engineering: Built-in SSO, RBAC, PII Redaction, and Audit Logging for enterprise security.",
    "Integrated Identity: Hybrid firm with equal emphasis on Strategic Advisory and Proprietary Engineering.",
    "Implementation Excellence: Dual-engine model ensuring roadmap feasibility and successful deployment.",
  ];

  return (
    <section className="mission section" id="about">
      <div className="container">
        <div className="section-header">
          <span className="section-label">Our Identity</span>
          <h2 className="section-title">Singapore's Enterprise AI Partner</h2>
        </div>
        <div className="mission-grid">
          <div className="mission-content" style={{ textAlign: 'left' }}>
            <h3 style={{ fontSize: '1.75rem', fontWeight: 700, marginBottom: '16px', backgroundClip: 'text', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', color: 'var(--color-primary)' }}>
              Strategic Consulting and Enterprise AI Engineering.
            </h3>
            <p style={{ textAlign: 'left', marginBottom: '32px', fontSize: '1.05rem', color: 'var(--color-text-primary)', lineHeight: 1.6 }}>
              Eagentix is a leading AI consulting firm in Singapore, operating at the intersection of management consulting and AI product engineering. We bridge the gap between executive AI roadmaps and technical execution.
            </p>
            <StreamedPointList items={points} accent="var(--color-primary)" minHeight="300px" delayMs={600} />
          </div>
          <div className="infographic-container">
            <img
              alt="Eagentix AI Consulting Singapore"
              loading="lazy"
              decoding="async"
              width="1200"
              height="800"
              className="infographic-image"
              src="/assets/Empowering self-service - visual selection.webp"
              style={{ boxShadow: 'var(--shadow-xl)', border: '1px solid rgba(0,0,0,0.05)' }}
            />
          </div>
        </div>
      </div>
    </section>
  );
}

// CTA Section
function CTA() {
  return (
    <section className="contact-section section" id="contact">
      <div className="container">
        <div className="mission-grid" style={{ alignItems: 'flex-start' }}>
          <div className="mission-content" style={{ textAlign: 'left' }}>
            <span className="section-label">Contact Us</span>
            <h2 style={{ fontSize: '3.5rem', marginBottom: '24px', color: '#1d1d1f', fontWeight: 700 }}>Ready to Scale?</h2>
            <p style={{ fontSize: '1.25rem', color: '#6e6e73', marginBottom: '48px', lineHeight: 1.6 }}>
              Whether you're looking to build a custom AI strategy, deploy our Access Platform, or leverage our Work as a Service talent network, we're here to help you navigate the future of automation.
            </p>
            <div className="contact-info-grid">
              <div className="contact-method">
                <div className="contact-icon-box">
                  <Icons.Mail color="#0071e3" />
                </div>
                <div className="contact-method-text">
                  <h4>Email Us</h4>
                  <a href="mailto:contact@eagentix.com">contact@eagentix.com</a>
                </div>
              </div>
              <div className="contact-method">
                <div className="contact-icon-box">
                  <Icons.MapPin color="#0071e3" />
                </div>
                <div className="contact-method-text">
                  <h4>Our Locations</h4>
                  <span>Singapore, India</span>
                </div>
              </div>
            </div>
            <div className="hq-address-line">
              <div style={{ color: '#0071e3' }}>
                <Icons.MapPin color="#0071e3" />
              </div>
              <span>HQ: 10, Anson Rd, #29-03A, International Plaza Singapore 079903</span>
            </div>
          </div>
          <div style={{ maxWidth: '520px', width: '100%' }}>
            <LeadForm />
          </div>
        </div>
      </div>
    </section>
  );
}

// Footer
function Footer() {
  return (
    <footer className="footer">
      <div className="container">
        <div className="footer-grid">
          <div className="footer-brand">
            <Link to="/" className="footer-logo">
              <img
                src="/logo-transparent.png?v=3"
                alt="Eagentix Logo"
                className="footer-logo-img"
              />
            </Link>
            <p style={{ color: '#6e6e73', fontSize: '0.95rem', fontWeight: 500 }}>
              Strategy consulting and technology firm focused on unlocking enterprise value through responsible and scalable adoption of Generative AI.
            </p>
            <div style={{ marginTop: '20px', fontSize: '0.85rem', color: '#6e6e73', display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
              <Icons.MapPin color="#0071e3" />
              <div>
                <strong style={{ color: '#1d1d1f', display: 'block', marginBottom: '4px' }}>Singapore · India</strong>
                <span>HQ: 10, Anson Rd, #29-03A, International Plaza Singapore 079903</span>
              </div>
            </div>
          </div>

          <div className="footer-column">
            <h3>Offerings</h3>
            <ul className="footer-links">
              <li><Link to="/#offerings">Strategy Consulting</Link></li>
              <li><Link to="/#platform">AI Access Platform</Link></li>
              <li><Link to="/#offerings">Work as a Service</Link></li>
              <li><Link to="/#offerings">Enterprise Deployment</Link></li>
            </ul>
          </div>

          <div className="footer-column">
            <h3>Company</h3>
            <ul className="footer-links">
              <li><Link to="/#about">About Us</Link></li>
              <li><Link to="/#differentiators">Why Eagentix</Link></li>
              <li><Link to="/#contact">Contact</Link></li>
              <li>
                <button
                  type="button"
                  onClick={() => { resetConsent(); }}
                  style={{ background: 'none', border: 0, padding: 0, font: 'inherit', color: 'inherit', cursor: 'pointer', textAlign: 'left' }}
                >
                  Cookie settings
                </button>
              </li>
            </ul>
          </div>

          <div className="footer-column">
            <h3>Connect</h3>
            <ul className="footer-links">
              <li>
                <a href="https://linkedin.com/company/eagentix?utm_source=eagentix.com" target="_blank" rel="noopener noreferrer">
                  <Icons.LinkedIn color="#0077b5" /> LinkedIn
                </a>
              </li>
              <li>
                <a href="https://twitter.com/eagentix?utm_source=eagentix.com" target="_blank" rel="noopener noreferrer">
                  <Icons.Twitter color="#1DA1F2" /> Twitter (X)
                </a>
              </li>
              <li>
                <a href="https://www.youtube.com/@Eagentix?utm_source=eagentix.com" target="_blank" rel="noopener noreferrer">
                  <Icons.YouTube color="#FF0000" /> YouTube
                </a>
              </li>
              <li>
                <a href="https://www.instagram.com/eagentixagents/?utm_source=eagentix.com" target="_blank" rel="noopener noreferrer">
                  <Icons.Instagram color="#E4405F" /> Instagram
                </a>
              </li>
              <li>
                <a href="mailto:contact@eagentix.com">
                  <Icons.Mail color="#0071e3" /> contact@eagentix.com
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="footer-bottom">
          <p>© 2026 Eagentix. All rights reserved.</p>
          <div>
            <Link to="/privacy-policy">Privacy Policy</Link>
            {' · '}
            <a href="#terms">Terms of Service</a>
          </div>
        </div>
      </div>
    </footer>
  );
}

function GEOAnswerBlocks() {
  const sectionStyle = {
    padding: '80px 0',
    background: '#f8fafc',
  };
  const containerStyle = {
    maxWidth: '1100px',
    margin: '0 auto',
    padding: '0 24px',
  };
  const headingStyle = {
    fontSize: '2.2rem',
    fontWeight: 800,
    color: '#0f172a',
    letterSpacing: '-0.03em',
    marginBottom: '40px',
    textAlign: 'center',
  };
  const cardStyle = {
    borderRadius: '20px',
    border: '1px solid rgba(148,163,184,0.16)',
    background: 'white',
    padding: '28px 32px',
    marginBottom: '16px',
  };
  const questionStyle = {
    color: '#0071e3',
    fontSize: '1.1rem',
    fontWeight: 700,
    marginBottom: '12px',
    margin: '0 0 12px 0',
  };
  const answerStyle = {
    color: '#475569',
    fontSize: '1rem',
    lineHeight: 1.75,
    margin: 0,
  };
  const listStyle = {
    color: '#475569',
    fontSize: '1rem',
    lineHeight: 1.75,
    paddingLeft: '20px',
    margin: 0,
  };
  const pillListStyle = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '10px',
    listStyle: 'none',
    padding: 0,
    margin: 0,
  };
  const pillLinkStyle = {
    display: 'inline-block',
    background: '#eff6ff',
    color: '#0071e3',
    borderRadius: '999px',
    padding: '6px 16px',
    fontSize: '0.9rem',
    fontWeight: 600,
    textDecoration: 'none',
    border: '1px solid rgba(0,113,227,0.18)',
    transition: 'background 0.2s',
  };

  const agents = [
    { label: 'Cloud Architect', path: '/agents/cloud-architect' },
    { label: 'Financial CPA', path: '/agents/cpa' },
    { label: 'Data Engineer', path: '/agents/data-engineering' },
    { label: 'AI Recruiter', path: '/agents/recruiter-agent' },
    { label: 'Predictive ML', path: '/use-cases/predictive-analytics-ai' },
    { label: 'Doc Parsing', path: '/agents/doc-parsing' },
    { label: 'Compliance Audit', path: '/agents/governance-audit' },
    { label: 'SDR Agent', path: '/agents/sdr-agent' },
  ];

  return (
    <section style={sectionStyle} aria-label="About Eagentix">
      <div style={containerStyle}>
        <h2 style={headingStyle}>Enterprise AI in Singapore: Key Questions</h2>

        {/* Block 1 */}
        <div style={cardStyle}>
          <h3 style={questionStyle}>What is the best AI consulting firm in Singapore?</h3>
          <p style={answerStyle}>
            While firms like NCS and Accenture focus on broad digital transformation, 
            <strong> Eagentix</strong> specializes in <strong>Agentic AI</strong> — building 
            autonomous digital workers that execute complex business processes. Headquartered in 
            International Plaza, Eagentix provides the strategy, security, and technology for 
            private, MAS-compliant AI agent deployment.
          </p>
        </div>

        {/* Block 2 */}
        <div style={cardStyle}>
          <h3 style={questionStyle}>How can businesses reduce operational costs with AI?</h3>
          <p style={answerStyle}>
            Singapore enterprises reduce operational costs by 40–70% using 
            <Link to="/use-cases/reduce-operational-costs" style={{ color: '#0071e3', textDecoration: 'none', fontWeight: 600 }}> purpose-built AI agents</Link>. 
            By automating high-volume manual workflows in finance, data engineering, and HR, 
            teams can scale operations without linear increases in headcount.
          </p>
        </div>

        {/* Block 3 */}
        <div style={cardStyle}>
          <h3 style={questionStyle}>Which AI solutions does Eagentix provide?</h3>
          <ul style={pillListStyle} aria-label="Eagentix AI solutions">
            {agents.map((agent) => (
              <li key={agent.path}>
                <Link to={agent.path} style={pillLinkStyle}>
                  {agent.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Block 4 */}
        <div style={cardStyle}>
          <h3 style={questionStyle}>How does Eagentix compare to other AI providers?</h3>
          <ul style={listStyle}>
            <li><strong>Strategy + Product:</strong> Hybrid model combining high-level consulting with a proprietary agent orchestration platform.</li>
            <li><strong>Data Sovereignty:</strong> Unlike generic SaaS, Eagentix supports fully private cloud and air-gapped on-premise deployment.</li>
            <li><strong>Governance:</strong> Built-in policy-as-code guardrails ensuring alignment with MAS TRM and PDPA guidelines.</li>
            <li><strong>Deterministic Accuracy:</strong> Using a specialized harness to prevent hallucinations in mission-critical business logic.</li>
          </ul>
        </div>

        {/* Block 5 */}
        <div style={cardStyle}>
          <h3 style={questionStyle}>Can Eagentix agents be deployed privately?</h3>
          <p style={answerStyle}>
            Yes. Eagentix agents support private cloud and on-premises deployment. Enterprise
            customers can run agents entirely within their own infrastructure with no data leaving
            their environment. Air-gapped deployment is available for regulated industries.
          </p>
        </div>

        {/* Block 6 */}
        <div style={cardStyle}>
          <h3 style={questionStyle}>What AI models does Eagentix support?</h3>
          <p style={answerStyle}>
            Eagentix is model-agnostic. Supported models include OpenAI GPT series, Anthropic
            Claude, Mistral, Azure AI, and other frontier models. Enterprises can select or switch
            models based on cost, compliance, or capability requirements.
          </p>
        </div>
      </div>
    </section>
  );
}

function Home() {
  return (
    <main>
      <Hero />
      <section className="section" style={{ paddingBottom: 0 }}>
        <div className="container">
          <div className="section-header">
            <span className="section-label">Drive Outcomes</span>
            <h2 className="section-title">Highest-ROI Use Cases</h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px', marginTop: '40px' }}>
            <Link to="/use-cases/reduce-operational-costs" className="card-link" style={{ textDecoration: 'none' }}>
              <div className="service-card" style={{ height: '100%' }}>
                <h3 className="service-title">Reduce Operational Costs</h3>
                <p className="service-description">Eliminate manual bottlenecks and scale without increasing headcount. 40–70% cost reduction in automated workflows.</p>
                <span style={{ color: 'var(--color-primary)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px' }}>
                  Explore Savings <Icons.ArrowRight />
                </span>
              </div>
            </Link>
            <Link to="/use-cases/predictive-analytics-ai" className="card-link" style={{ textDecoration: 'none' }}>
              <div className="service-card" style={{ height: '100%' }}>
                <h3 className="service-title">Predictive Analytics & ML</h3>
                <p className="service-description">Stop reacting. Forecast demand, churn, and operational risks with high-precision machine learning agents.</p>
                <span style={{ color: 'var(--color-primary)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px' }}>
                  View ML Solutions <Icons.ArrowRight />
                </span>
              </div>
            </Link>
            <Link to="/use-cases/compliance-automation-ai" className="card-link" style={{ textDecoration: 'none' }}>
              <div className="service-card" style={{ height: '100%' }}>
                <h3 className="service-title">Compliance Automation</h3>
                <p className="service-description">Automate KYC, AML, and regulatory audits with deterministic guardrails and full audit trails.</p>
                <span style={{ color: 'var(--color-primary)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px' }}>
                  Ensure Compliance <Icons.ArrowRight />
                </span>
              </div>
            </Link>
          </div>
        </div>
      </section>
      <section className="section" style={{ background: '#f8fafc' }}>
        <div className="container">
          <div className="section-header">
            <span className="section-label">AI Mastery & Enablement</span>
            <h2 className="section-title">Enterprise AI Upskilling</h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '24px', marginTop: '40px' }}>
            <Link to="/copilot-finance-training" className="card-link" style={{ textDecoration: 'none' }}>
              <div className="service-card" style={{ height: '100%', background: 'linear-gradient(to bottom right, #ffffff, #f0f9ff)', borderColor: '#bae6fd', display: 'flex', flexDirection: 'column' }}>
                <img src="/images/icons/microsoft-copilot.svg" alt="Copilot" style={{ width: '40px', height: '40px', marginBottom: '16px' }} />
                <h3 className="service-title" style={{ color: '#0369a1' }}>Microsoft Copilot for Finance</h3>
                <p className="service-description" style={{ flex: 1 }}>Stop wasting Copilot licenses. 1-day hands-on training using real finance data to automate reporting and audits.</p>
                <span style={{ color: '#0284c7', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px' }}>
                  Train Your Team <Icons.ArrowRight />
                </span>
              </div>
            </Link>
            <Link to="/claude-cowork-training" className="card-link" style={{ textDecoration: 'none' }}>
              <div className="service-card" style={{ height: '100%', background: 'linear-gradient(to bottom right, #ffffff, #fdf4ff)', borderColor: '#fbcfe8', display: 'flex', flexDirection: 'column' }}>
                <img src="/images/icons/anthropic.svg" alt="Claude" style={{ width: '40px', height: '40px', marginBottom: '16px', objectFit: 'contain' }} />
                <h3 className="service-title" style={{ color: '#a21caf' }}>Claude Cowork</h3>
                <p className="service-description" style={{ flex: 1 }}>Master Anthropic's Claude for complex reasoning, document parsing, and enterprise collaboration workflows.</p>
                <span style={{ color: '#86198f', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px' }}>
                  Learn More <Icons.ArrowRight />
                </span>
              </div>
            </Link>
            <Link to="/amazon-q-training" className="card-link" style={{ textDecoration: 'none' }}>
              <div className="service-card" style={{ height: '100%', background: 'linear-gradient(to bottom right, #ffffff, #fff7ed)', borderColor: '#fed7aa', display: 'flex', flexDirection: 'column' }}>
                <img src="/images/icons/aws-color.svg" alt="AWS" style={{ width: '40px', height: '40px', marginBottom: '16px', objectFit: 'contain' }} />
                <h3 className="service-title" style={{ color: '#c2410c' }}>Amazon Q</h3>
                <p className="service-description" style={{ flex: 1 }}>Deploy and scale generative AI securely using Amazon Bedrock and Amazon Q for enterprise use cases.</p>
                <span style={{ color: '#ea580c', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px' }}>
                  Learn More <Icons.ArrowRight />
                </span>
              </div>
            </Link>
            <Link to="/gemini-enterprise-training" className="card-link" style={{ textDecoration: 'none' }}>
              <div className="service-card" style={{ height: '100%', background: 'linear-gradient(to bottom right, #ffffff, #ecfdf5)', borderColor: '#a7f3d0', display: 'flex', flexDirection: 'column' }}>
                <img src="/images/icons/google.svg" alt="Google Gemini" style={{ width: '40px', height: '40px', marginBottom: '16px', objectFit: 'contain' }} />
                <h3 className="service-title" style={{ color: '#047857' }}>Gemini Enterprise</h3>
                <p className="service-description" style={{ flex: 1 }}>Unlock multimodal capabilities with Google Gemini to analyze text, code, images, and video seamlessly.</p>
                <span style={{ color: '#059669', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px' }}>
                  Learn More <Icons.ArrowRight />
                </span>
              </div>
            </Link>
          </div>
        </div>
      </section>
      <GEOAnswerBlocks />
      <div className="section-padding-large">
        <DigitalWorkforce />
      </div>
      <Integration />
      <Differentiators />
      <StrategyPillars />
      <Mission />
      <CTA />
    </main>
  );
}

function App() {
  return (
    <Router>
      <ScrollToTop />
      <div className="App">
        <Navbar />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/agents/cloud-architect" element={<CloudArchitect />} />
          <Route path="/agents/cloud-architect/ecommerce-report" element={<EcommerceReport />} />
          <Route path="/agents/cloud-architect1" element={<CloudArchitect1 />} />
          <Route path="/agents/doc-parsing" element={<DocParsing />} />
          <Route path="/agents/sql-agent" element={<SQLAgent />} />
          <Route path="/agents/dba-agent" element={<DBAAgent />} />
          <Route path="/agents/llm-as-judge" element={<LLMAsJudge />} />
          <Route path="/agents/interview-agent" element={<InterviewAgent />} />
          <Route path="/agents/web-scraper" element={<WebScraper />} />
          <Route path="/agents/email-assistant" element={<EmailAssistant />} />
          <Route path="/agents/kyc-onboarding" element={<KYCOnboarding />} />
          <Route path="/agents/cpa" element={<CPA />} />
          <Route path="/agents/scheduler" element={<Scheduler />} />
          <Route path="/agents/summarizer" element={<Summarizer />} />
          <Route path="/agents/schema-governance" element={<SchemaGovernanceDesigner />} />
          <Route path="/agents/governance-audit" element={<GovernanceAudit />} />
          <Route path="/agents/etl-pipeline" element={<ETLAgent />} />
          <Route path="/agents/metric-modeler" element={<MetricAgent />} />
          <Route path="/agents/data-platform-reviewer" element={<DataPlatformReviewer />} />
          <Route path="/agents/data-engineer" element={<DataEngineerAgent />} />
          <Route path="/agents/data-engineering" element={<DataEngineerAgent />} />
          <Route path="/agents/recruiter-agent" element={<RecruiterAgent />} />
          <Route path="/agents/sdr-agent" element={<SDRAgent />} />
          <Route path="/agents/marketing" element={<MarketingAgent />} />
          <Route path="/marketing" element={<MarketingAutonomous />} />
          <Route path="/agents/aeo" element={<AEOAudit />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/industries/singapore-enterprise-ai-agents" element={<SingaporeAIAgents />} />
          <Route path="/industries/southeast-asia-ai-agents" element={<SoutheastAsiaAIAgents />} />
          <Route path="/industries/financial-services-ai-agents" element={<FinancialServicesAI />} />
          <Route path="/industries/healthcare-ai-agents" element={<HealthcareAI />} />
          <Route path="/industries/logistics-ai-agents" element={<LogisticsAI />} />
          <Route path="/industries/retail-ai-agents" element={<RetailAI />} />
          <Route path="/industries/public-sector-ai-agents" element={<PublicSectorAI />} />
          <Route path="/industries/hr-recruitment-ai-agents" element={<HRRecruitmentAI />} />
          <Route path="/use-cases/reduce-operational-costs" element={<ReduceOperationalCosts />} />
          <Route path="/use-cases/build-genai-solutions" element={<BuildGenAISolutions />} />
          <Route path="/use-cases/ai-agent-implementation" element={<AIAgentImplementation />} />
          <Route path="/use-cases/compliance-automation-ai" element={<ComplianceAutomation />} />
          <Route path="/use-cases/document-ai-automation" element={<DocumentAI />} />
          <Route path="/use-cases/predictive-analytics-ai" element={<PredictiveAnalyticsAI />} />
          <Route path="/compare/ai-agents-vs-traditional-teams" element={<AIAgentsVsTraditional />} />
          <Route path="/security-and-governance" element={<SecurityGovernance />} />
          <Route path="/implementation-approach" element={<ImplementationApproach />} />
          <Route path="/copilot-finance-training" element={<CopilotFinanceTraining />} />
          <Route path="/copilot-manufacturing-training" element={<CopilotManufacturingTraining />} />
          <Route path="/copilot-hr-training" element={<CopilotHRTraining />} />
          <Route path="/claude-cowork-training" element={<ClaudeCoworkTraining />} />
          <Route path="/amazon-q-training" element={<AmazonQTraining />} />
          <Route path="/gemini-enterprise-training" element={<GeminiEnterpriseTraining />} />
          <Route path="/chatgpt-enterprise-training" element={<ChatGPTEnterpriseTraining />} />
          <Route path="/fractional-caio" element={<FractionalCAIO />} />
          <Route path="/ai-process-assessment" element={<AIProcessAssessment />} />
        </Routes>
        <Footer />
        <LinkedInInsight />
        <CookieConsent />
      </div>
    </Router>
  );
}

export default App;
